import {
  CACHE_MANAGER,
  getDragOffset,
  getTouchDistance,
  isIos,
  createButton,
} from "../utils.js";
import ViewerLayer from "./ViewerLayer.js";
import Layer from "./Layer.js";
import CarouselLayer from "./CarouselLayer.js";
import Renderer from "./Renderer.js";
import { setupZoomControls } from "../zoom-controls.js";
import { setupLayersControls } from "../layers-controls.js";
import { setupHotspotsControl } from "../hotspots-control.js";

import KUTE from "kute.js";
import { Viewer } from "@photo-sphere-viewer/core";
import Ajv2019 from "ajv/dist/2019.js";
import * as spinnerDataSchema from "../spinner.schema.json";
import ZoomHandler from "./ZoomHandler.js";

const ajv = new Ajv2019();
const validateData = ajv.compile(spinnerDataSchema);

const RESOLUTION_480P = 720;
const RESOLUTION_720P = 1200;
const RESOLUTION_1080P = 1920;

const RESOLUTION_30MP = 6720;

const SPINNABLE_LAYERS = ["default", "doors", "lights", "showcase"];

export const VIEWER_MODE_PORTRAIT = "portrait";
export const VIEWER_MODE_LANDSCAPE = "landscape";
export const VIEWER_MODE_BOUNDING_BOX = "bounding-box";

export const OPACITY_1_CSS_TEXT = ";opacity:1";
export const OPACITY_0_CSS_TEXT = ";opacity:0";
const INSPECTOR_MODE_CLASSNAME = "inspector-mode";

const DEFAULT_SPINNING_SPEED = 1;
const DEFAULT_MAX_ZOOM = 6;

// Define UIConfig type in jsdoc
/**
 * @typedef {object} UIConfig
 * @property {number} [spinningSpeed]
 * @property {number} [maxZoom]
 * @property {string} [buttonsPlacement]
 * @property {string} [viewerMode]
 * @property {string} [canScroll]
 * @property {string} [canFullscreen]
 * @property {string} [defaultLayer]
 * @property {boolean} [cleanUi]
 * @property {boolean} [hideGallery]
 * @property {boolean} [hideDoors]
 * @property {boolean} [hideLights]
 * @property {boolean} [hideShowcase]
 * @property {boolean} [hideInterior]
 * @property {boolean} [hideZoom]
 * @property {boolean} [hideHotspots]
 */

/**
 *
 * @param {HTMLElement|ShadowRoot} root
 */

/**
 * @param {*} root
 */
class Spinner {
  constructor(root) {
    this.root = root;
    this.uiX = 0;
    this.uiY = 0;
    this.uiWidth = 0;
    this.uiHeight = 0;
    this.elById = (id) => {
      return root.getElementById(id);
    };

    this.spinnerNode = this.elById("spinner");
    this.sceneNode = this.elById("scene");
    this.flashScreenNode = this.elById("flash-screen");
    this.busyIndicatorNode = this.elById("busy-indicator");
    this.contextMenuNode = this.elById("context-menu");
    this.viewerLayerNode = this.elById("viewer-layer");
    this.zoomLayerNode = this.elById("zoom-layer");
    this.interiorLayerNode = this.elById("interior-layer");
    this.hotspotsLayerNode = this.elById("hotspots-layer");
    this.controlsLayerNode = this.elById("controls-layer");
    this.carouselLayerNode = this.elById("carousel-layer");
    this.loaderLayerNode = this.elById("loader-layer");
    this.brandNode = this.elById("brand");

    this.viewerLayer = new ViewerLayer(this, this.viewerLayerNode);
    this.zoomLayer = new Layer(this, this.zoomLayerNode);
    this.zoomLayerSubject = this.zoomLayer.subject;
    this.interiorLayer = new Layer(this, this.interiorLayerNode);
    /**
     *
     * @type {Layer}
     */
    this.hotspotsLayer = new Layer(this, this.hotspotsLayerNode);
    this.controlsLayer = new Layer(this, this.controlsLayerNode);
    this.loaderLayer = new Layer(this, this.loaderLayerNode);
    this.carouselLayer = new CarouselLayer(this, this.carouselLayerNode);

    this.renderer = new Renderer(this);

    this.dragEvent = null;
    this.startTouchEvent = null;
    this.spinningStarted = false;
    this.zoomScale = 1;
    this.viewportOffset = { x: 0, y: 0, originX: 0, originY: 0 };
    this.contentWidth = 0;
    this.contentHeight = 0;
    this.transformOriginCSS = "0 0 0";
    this.cleanUi = false;
    this.initialImageSize =
      this.spinnerNode.clientWidth <= RESOLUTION_480P
        ? RESOLUTION_480P
        : RESOLUTION_720P;
    this.supportsScroll = true;
    this.supportsFullscreen = true;
    this.layerButtons = {};
    this.defaultLayer = null;
    this.activeLayer = null;
    this.activeRow = 0;
    this.activeColumn = 0;
    this.activeImage = null;
    this.spinningSpeed = DEFAULT_SPINNING_SPEED;
    this.maxZoom = DEFAULT_MAX_ZOOM;
    this.AVAILABLE_VIDEOS = {};
    this.defaultViewerMode = VIEWER_MODE_PORTRAIT;
    this.interiorLayer.visibility = false;
    this.hotspotsActive = true;
    this.boundingBoxWidthRatio = 1;
    this.boundingBoxHeightRatio = 1;
    this.zoomHandler = new ZoomHandler(this);

    this.bindEventHandlers();

    this._viewerMode = null;
    /** @type {any} */
    this._uiInteriorEnterTween = null;
    /** @type {any} */
    this._uiInteriorLeaveTween = null;
    /** @type {any} */
    this._activeLayerButton = null;
  }

  bindEventHandlers() {
    this._onWheel = this.onWheel.bind(this);
    this._onDragStart = this.onDragStart.bind(this);
    this._onDragEnd = this.onDragEnd.bind(this);
    this._onMouseMove = this.onMouseMove.bind(this);
    this._onTouchMove = this.onTouchMove.bind(this);
    this._onPointerDown = this.onPointerDown.bind(this);
    this._onMouseDown = this.onMouseDown.bind(this);
    this._onContextMenu = this.onContextMenu.bind(this);
    this._onWindowResize = this.onWindowResize.bind(this);
  }

  set inspectorMode(flag) {
    if (flag) {
      if (this.spinnerNode.classList.contains(INSPECTOR_MODE_CLASSNAME)) return;

      this.spinnerNode.classList.add(INSPECTOR_MODE_CLASSNAME);
      const distance = -20;
      const layers = this.spinnerNode.querySelectorAll(".layer");
      layers.forEach((layer, i) => {
        const currentAmount = distance + i * 5;
        layer.oldCssText = layer.style.cssText;
        layer.style.cssText = ` transform: scale(0.69) rotateY(50deg) rotateX(-5deg) rotateZ(-6deg)
          translateZ(${currentAmount}em) translateY(${currentAmount}em);`;
      });
    } else {
      this.spinnerNode.classList.remove(INSPECTOR_MODE_CLASSNAME);
      const layers = this.spinnerNode.querySelectorAll(".layer");
      layers.forEach((layer) => {
        layer.style.cssText = layer.oldCssText;
      });
    }
  }

  get zoomPointX() {
    return this.zoomHandler.zoomPointX;
  }

  set zoomPointX(value) {
    this.zoomHandler.zoomPointX = value;
  }

  get zoomPointY() {
    return this.zoomHandler.zoomPointY;
  }

  set zoomPointY(value) {
    this.zoomHandler.zoomPointY = value;
  }

  set notFound(value) {
    if (value) {
      this.spinnerNode.classList.add("not-found");
    } else {
      this.spinnerNode.classList.remove("not-found");
    }
  }

  get notFound() {
    return this.spinnerNode.classList.contains("not-found");
  }

  set uiBusyEnabled(value) {
    this.busyIndicatorNode.style.display = value ? "block" : "none";
  }

  get smoothTransform() {
    return this.viewerLayer.smoothTransform;
  }

  set smoothTransform(value) {
    this.viewerLayer.smoothTransform = value;
    this.zoomLayer.smoothTransform = value;
    this.hotspotsLayer.smoothTransform = value;
  }

  set uiControlsEnabled(value) {
    this.controlsLayer.disabled = !value;
  }

  set uiHotspotsEnabled(value) {
    this.hotspotsLayer.disabled = !value;
  }

  get uiHotspotsEnabled() {
    return this.hotspotsLayer.disabled;
  }

  set uiContextMenuVisibility(value) {
    if (value) {
      this.contextMenuNode.style.display = null;
      const appVersionNode = this.spinnerNode.querySelector("#app-version");

      setTimeout(() => {
        try {
          navigator.clipboard
            .write([
              new ClipboardItem({
                "text/plain": appVersionNode.innerText,
              }),
            ])
            .catch(console.error);
        } catch (_e) {
          const textArea = document.createElement("textarea");
          textArea.value = appVersionNode.innerText;

          // Avoid scrolling to bottom
          textArea.style.position = "fixed";
          textArea.style.top = "0";
          textArea.style.left = "0";
          textArea.style.opacity = "0";

          this.spinnerNode.appendChild(textArea);
          textArea.focus();
          textArea.select();

          try {
            // eslint-disable-next-line etc/no-deprecated
            const successful = document.execCommand("copy");
            if (successful) {
              console.log("Fallback: Text copied to clipboard");
            } else {
              console.error("Fallback: Unable to copy");
            }
          } catch (err) {
            console.error("Fallback: Oops, unable to copy", err);
          }

          this.spinnerNode.removeChild(textArea);
        }
      }, 50);
    } else {
      this.contextMenuNode.style.display = "none";
    }
  }

  set buttonsPlacement(value) {
    this.controlsLayer.host.className = `layer layer-controls ${value}`;
  }

  /**
   *
   * @param {string|null} value
   */
  set viewerMode(value) {
    this._viewerMode = value;
    this.spinnerNode.classList.remove(
      VIEWER_MODE_PORTRAIT,
      VIEWER_MODE_LANDSCAPE,
      VIEWER_MODE_BOUNDING_BOX,
    );

    if (value) {
      this.spinnerNode.classList.add(value);
    }
  }

  get viewerMode() {
    return this._viewerMode;
  }

  set uiFullscreenEnabled(value) {
    const fullscreenElement =
      document.fullscreenElement ?? document.webkitFullscreenElement;
    if (value && !fullscreenElement) {
      this.spinnerNode.requestFullscreen();
      this.spinnerNode.classList.add("fullscreen");
    } else if (!value && fullscreenElement) {
      const exitFullscreen =
        "exitFullscreen" in document
          ? document.exitFullscreen.bind(document)
          : document.webkitExitFullscreen.bind(document);
      exitFullscreen().catch(console.error);
      this.spinnerNode.classList.remove("fullscreen");
    }
  }

  get uiFullscreenEnabled() {
    return Boolean(
      document.fullscreenElement ?? document.webkitFullscreenElement,
    );
  }

  get uiInteriorModeEnabled() {
    return this.interiorLayer.opacity === "1";
  }

  set uiInteriorModeEnabled(value) {
    if (value) {
      const fadeIn = () => {
        if (this.interiorLayer.opacity === "1") return;
        if (this._uiInteriorLeaveTween) {
          this._uiInteriorLeaveTween.stop();
        }

        this._uiInteriorEnterTween = KUTE.fromTo(
          this.interiorLayer.host,
          {
            opacity: 0,
          },
          {
            opacity: 1,
          },
          {
            delay: 200,
            easing: "easingQuinticOut",
            duration: 500,
            onStart: () => {
              this.loaderLayer.visibility = false;
              this.interiorLayer.visibility = true;
              this.interiorViewer.resize();
              this.uiControlsEnabled = true;
              this.interiorLayerNode.style.pointerEvents = null;
            },
          },
        ).start();
      };

      if (this.interiorViewer) {
        fadeIn();
      } else {
        /*
         * A customer overrides the native Request object, which cause
         * the interior to break. This is a workaround for this scenario.
         */
        const customerRequestClass = window.Request;
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        document.body.appendChild(iframe);

        const nativeRequestClass = iframe.contentWindow.Request;
        document.body.removeChild(iframe);
        window.Request = nativeRequestClass;

        // Make the interior viewer and then start the fade in animation
        this.loaderLayer.visibility = true;
        this.interiorLayer.host.style.opacity = "0";
        this.interiorLayer.visibility = true;

        const config = {
          container: this.interiorLayer.subject,
          defaultZoomLvl: 50,
          mousewheel: true,
          minFov: 75,
          maxFov: 75,
          keyboard: false,
          navbar: false,
        };

        if (this.interiorImageMaxResolution > RESOLUTION_30MP) {
          config.defaultZoomLvl = 0;
          config.minFov = 30;
          config.maxFov = 90;
        }

        this.interiorViewer = new Viewer(config);
        this.interiorViewer.textureLoader
          .loadImage(this.interiorImageUrl)
          .then((p) => {
            this.interiorViewer
              .setPanorama(p.src, {
                panoData: {
                  fullWidth: p.naturalWidth,
                  fullHeight: p.naturalWidth / 2,
                },
              })
              .catch(console.error);
          })
          .catch((e) => {
            console.error(e);
          });

        this.interiorViewer.addEventListener("ready", () => {
          fadeIn();
          window.Request = customerRequestClass;
        });
      }
    } else if (this.interiorViewer) {
      this.interiorLayerNode.style.pointerEvents = "none";
      void this.interiorViewer.stopAnimation();

      if (this._uiInteriorEnterTween) {
        this._uiInteriorEnterTween.stop();
      }

      this._uiInteriorLeaveTween = KUTE.fromTo(
        this.interiorLayer.host,
        {
          opacity: 1,
        },
        {
          opacity: 0,
        },
        {
          easing: "easingQuinticOut",
          duration: 500,
          onComplete: () => {
            this.interiorLayer.visibility = false;
          },
        },
      ).start();
    }
  }

  on(event, handler, options) {
    this.spinnerNode.addEventListener(event, handler, options);
  }

  off(event, handler, options) {
    this.spinnerNode.removeEventListener(event, handler, options);
  }

  /**
   *
   * @param {CustomEvent} event
   */
  dispatchEvent(event) {
    this.spinnerNode.dispatchEvent(event);
  }

  canActivateLayer(layer) {
    switch (layer.type) {
      case "doors":
        return !this.hideDoors;
      case "lights":
        return !this.hideLights;
      case "showcase":
        return !this.hideShowcase;
      case "interior":
        return !this.hideInterior;
      default:
        return true;
    }
  }

  /**
   *
   * @param {string} value
   */
  async setActiveLayer(value) {
    if (!this.canActivateLayer(this.layers[value])) {
      if (!this.canActivateLayer(this.layers[this.defaultLayerType])) {
        throw new Error(
          `Layer "${value}" is not available and fall back layer "${this.defaultLayerType}" is not available either`,
        );
      }

      value = this.defaultLayerType;
    }

    if (this._activeLayerButton) {
      this._activeLayerButton.classList.remove("active");
    }

    this._activeLayerButton = this.layerButtons[value];

    if (this._activeLayerButton) {
      this._activeLayerButton.classList.add("active");
    }

    const chosenLayer = this.layers[value];
    if (!chosenLayer.type) {
      throw new Error(
        `Selected layer is missing type. Expected type: ${value}`,
      );
    }

    if (this.activeLayer === chosenLayer && this.viewerLayer.subject.src) {
      return;
    }

    this.activeLayer = chosenLayer;

    await this.setPoster();

    if (this.activeLayer.type === "interior") {
      this.uiInteriorModeEnabled = true;
      this.hotspotsLayer.visibility = false;
    } else {
      this.uiInteriorModeEnabled = false;
      await this.loadLayerContent();

      if (this.hotspotsActive && !this.cleanUi) {
        this.hotspotsLayer.visibility = true;
      }
    }

    this.dispatchEvent(
      new CustomEvent("layerchange", {
        composed: true,
        detail: {
          layer: this.activeLayer,
          oldLayer: chosenLayer,
        },
      }),
    );
  }

  /**
   * Creates and add a button to the `buttons` area of control layer.
   * Upon click, it will activate specified layer and the button stays active.
   * Only one layer button can be active at the time
   * @param {"default"|"doors"|"lights"|"showcase"|"interior"} layer
   * @returns {HTMLButtonElement}
   */
  addLayerButton(layer) {
    const btn = createButton(layer);
    this.layerButtons[layer] = btn;

    btn.onpointerup = (event) => {
      if (event.button === 2) return;

      this.setActiveLayer(layer)
        .then(() => {
          this.applyTransform();
        })
        .catch(console.error);
    };

    this.controlsLayer.appendTo(btn, "buttons");

    return btn;
  }

  /**
   *
   * @param {string} id
   * @param {Function} action
   * @param {boolean} [appendImmediately]
   * @returns {HTMLButtonElement}
   */
  addActionButton(id, action, appendImmediately = true) {
    const btn = createButton(id);

    btn.onpointerup = (event) => {
      if (event.button === 2) return;

      action.call(this, event);
    };

    if (appendImmediately) {
      this.controlsLayer.appendTo(btn, "buttons");
    }

    return btn;
  }

  /**
   *
   * @param {UIConfig} uiConfig
   */
  applyUiConfig(uiConfig) {
    this.spinningSpeed = uiConfig.spinningSpeed ? uiConfig.spinningSpeed : 1;
    this.maxZoom = uiConfig.maxZoom ? uiConfig.maxZoom : 6;
    this.buttonsPlacement = uiConfig.buttonsPlacement ?? "bottom";
    this.supportsScroll = uiConfig.canScroll
      ? uiConfig.canScroll === "true"
      : true;
    this.supportsFullscreen = uiConfig.canFullscreen
      ? uiConfig.canFullscreen === "true"
      : true;
    this.defaultLayerType = uiConfig.defaultLayer ?? this.defaultLayerType;
    this.cleanUi = uiConfig.cleanUi ?? false;
    this.hideGallery = uiConfig.hideGallery ?? false;
    this.hideDoors = uiConfig.hideDoors ?? false;
    this.hideLights = uiConfig.hideLights ?? false;
    this.hideShowcase = uiConfig.hideShowcase ?? false;
    this.hideInterior = uiConfig.hideInterior ?? false;
    this.hideZoom = uiConfig.hideZoom ?? false;
    this.hideHotspots = uiConfig.hideHotspots ?? false;

    this.populateDefaults();
    this.applyViewerMode(uiConfig.viewerMode);
    this.buildUserInterface();
  }

  /**
   *
   * @param {string} hash
   * @param {UIConfig} [uiConfig]
   * @returns {Promise<void>}
   */
  async setHash(hash, uiConfig) {
    if (!hash) {
      return;
    }

    this.loaderLayer.visibility = true;
    const response = await fetch(
      `${import.meta.env.VITE_SPINNER_DATA_URL}${hash}.json`,
    );

    this.notFound = !response.ok;

    const json = await response.json();

    if (validateData(json)) {
      this.setData(json);
      this.applyUiConfig(uiConfig);
    } else {
      console.warn(validateData.errors);
    }

    return json;
  }

  populateDefaults() {
    this.defaultLayer = this.activeLayer || this.layers[this.defaultLayerType];
    if (!this.defaultLayer) {
      throw new Error(`Default layer "${this.defaultLayerType}" not found`);
    }

    this.activeRow = this.activeRow || this.defaultLayer.default_row;
    this.activeColumn = this.activeColumn || this.defaultLayer.default_column;
    this.activeImage = null;
    this.aspectRatioCSS = null;
  }

  setData(data) {
    this.productName = data.product_name;
    this.clientName = data.client_name;
    this.spinnerHash = data.spinner_hash;
    this.defaultLayerType = data.default_layer;
    this.imagesBaseUrl = data.images_base_url;
    this.availableLabels = data.available_labels || [];
    this.defaultViewerMode = data.viewer_mode ?? VIEWER_MODE_PORTRAIT;
    this.intellicrops = data.intellicrops || {};
    this.galleryImages = data.gallery || [];
    this.showBranding = data.show_branding;
    this.layers = data.layers;
    this.hasInterior = Boolean(data.interior_image_url || data.interior_image);
    this.interiorImage = data.interior_image;
    this.hotspots = data.hotspots || {};
    this.hasHotspots =
      Object.keys(this.hotspots).length > 0 &&
      Object.keys(this.intellicrops).length > 0;
    this.placeholder = data.placeholder;
    this.boundingBox = data.bounding_box;
    this.interiorImageMaxResolution =
      data.interior_image_max_resolution || RESOLUTION_30MP;

    /* eslint-disable @typescript-eslint/naming-convention */
    if (this.hasInterior) {
      // To reduce antipattern, we make a layer out of the interior image
      this.layers.interior = {
        type: "interior",
        columns: 1,
        rows: 1,
        default_column: 1,
        default_row: 1,
        images: {
          "1-1": {
            id: 1,
            hash: data.interior_image,
            url: data.interior_image_url,
          },
        },
      };
    }
    /* eslint-enable @typescript-eslint/naming-convention */

    this.interiorImageUrl =
      data.interior_image_url ||
      `${this.imagesBaseUrl}/${this.interiorImage}.${this.interiorImageMaxResolution}`;
    this.availableLayers = Object.keys(this.layers);

    this.activeLayer = null;
    this.activeRow = 0;
    this.activeColumn = 0;

    this.root.dispatchEvent(
      new CustomEvent("datachange", {
        composed: true,
        detail: {
          customerData: {
            productName: this.productName,
            clientName: this.clientName,
          },
        },
      }),
    );
  }

  onPointerDown(event) {
    if (!this.controlsLayer.host.contains(event.target)) {
      this.controlsLayer.subject.classList.remove("expand");
    }
  }

  onMouseDown(event) {
    if (!this.contextMenuNode.contains(event.target) && event.button === 0) {
      this.uiContextMenuVisibility = false;
    }
  }

  onContextMenu(event) {
    event.preventDefault();
    this.contextMenuNode.style.left = `${event.clientX}px`;
    this.contextMenuNode.style.top = `${event.clientY}px`;
    this.uiContextMenuVisibility = true;
    return false;
  }

  installEventHandlers() {
    this.root.host.removeEventListener("wheel", this._onWheel, false);
    if (this.supportsScroll) {
      this.root.host.addEventListener("wheel", this._onWheel, false);
      this.root.host.addEventListener(
        "wheel",
        () => {
          this.spinnerNode.classList.add("ready");
        },
        { once: true },
      );
    }

    window.removeEventListener("resize", this._onWindowResize);
    window.addEventListener("resize", this._onWindowResize);
    window.removeEventListener("scroll", this._onWindowResize);
    window.addEventListener("scroll", this._onWindowResize);

    this.zoomLayer.off("mousedown", this._onDragStart, false);
    this.zoomLayer.on("mousedown", this._onDragStart, false);

    window.removeEventListener("mouseleave", this._onDragEnd, false);
    window.addEventListener("mouseleave", this._onDragEnd, false);

    window.removeEventListener("mouseup", this._onDragEnd, false);
    window.addEventListener("mouseup", this._onDragEnd, false);

    this.zoomLayer.off("touchstart", this._onDragStart, false);
    this.zoomLayer.on("touchstart", this._onDragStart, false);

    this.zoomLayer.off("touchcancel", this._onDragEnd, false);
    this.zoomLayer.on("touchcancel", this._onDragEnd, false);

    this.zoomLayer.off("touchend", this._onDragEnd, false);
    this.zoomLayer.on("touchend", this._onDragEnd, false);

    window.removeEventListener("mousemove", this._onMouseMove, false);
    window.addEventListener("mousemove", this._onMouseMove, false);

    this.zoomLayer.off("touchmove", this._onTouchMove, false);
    this.zoomLayer.on("touchmove", this._onTouchMove, false);

    this.off("pointerdown", this._onPointerDown);
    this.on("pointerdown", this._onPointerDown);

    this.off("mousedown", this._onMouseDown, false);
    this.on("mousedown", this._onMouseDown, false);

    this.off("contextmenu", this._onContextMenu, false);
    this.on("contextmenu", this._onContextMenu, false);

    this.on(
      "pointerdown",
      () => {
        this.spinnerNode.classList.add("ready");
      },
      { once: true },
    );
  }

  buildControls() {
    this.controlsLayer.subjectChildren.buttons.innerHTML = "";

    if (this.availableLayers.length > 1) {
      const menuButton = this.addActionButton("menu", () => {
        this.controlsLayer.subject.classList.add("inert");
        requestAnimationFrame(() => {
          this.controlsLayer.subject.classList.add("expand");
          setTimeout(() => {
            this.controlsLayer.subject.classList.remove("inert");
          }, 500);
        });
      });
      menuButton.tabIndex = -1;
    }

    const of = this.addActionButton("fullscreen", () => {
      this.uiFullscreenEnabled = true;
    });
    of.classList.add("fullscreen-supported", "window-only");

    const cf = this.addActionButton("close-fullscreen", () => {
      this.uiFullscreenEnabled = false;
    });
    cf.classList.add("fullscreen-supported", "fullscreen-only");

    this.spinnerNode.classList.remove("supports-fullscreen");

    if (this.supportsFullscreen && document.fullscreenEnabled) {
      this.spinnerNode.classList.add("supports-fullscreen");
    }

    setupZoomControls(this);

    setupLayersControls(this);

    setupHotspotsControl(this, false);

    /*
     * Hide the menu on iOS if there is only one layer
     * since iOS does not support fullscreen
     */
    if (
      isIos() &&
      this.availableLayers.length === 1 &&
      !this.hasInterior &&
      !this.hasHotspots &&
      !this.galleryImages.length
    ) {
      this.controlsLayer.subject.classList.add("no-controls");
    }

    this.brandNode.style.display = this.showBranding ? "" : "none";
  }

  buildUserInterface() {
    this.renderer.reset();
    this.renderer.addOnUpdate(() => {
      this.dispatchEvent(
        new CustomEvent("framechange", {
          composed: true,
          detail: {
            transformOrigin: this.transformOriginCSS,
            activeColumn: this.activeColumn,
            activeRow: this.activeRow,
            image: this.activeImage,
          },
        }),
      );
    });

    this.sceneNode.style.opacity = 0;
    this.hotspotsLayer.visibility = !this.cleanUi;
    this.controlsLayer.visibility = !this.cleanUi;

    if (this.carouselLayer.isOpen) {
      this.carouselLayer.close();
    }

    if (this.interiorViewer) {
      this.interiorViewer.destroy();
      this.interiorViewer = null;
    }

    const variant = this.defaultLayer.rows > 1 ? "variant-3d" : "variant-360";
    this.spinnerNode.classList.remove("variant-3d", "variant-360");
    this.spinnerNode.classList.add(variant);

    this.buildControls();

    this.installEventHandlers();

    this.setActiveLayer(this.activeLayer?.type || this.defaultLayerType)
      .then(() => {
        this.smoothTransform = false;
        /*
         * We need to calculate the layout when spinner
         * loads a layer for the first time
         */
        this.recalculateLayout(() => {
          this.applyTransform();
          this.smoothTransform = true;

          this.sceneNode.animate(
            { opacity: 1 },
            {
              duration: 100,
              fill: "both",
            },
          );
        });
      })
      .catch(console.error);
  }

  onWindowResize() {
    clearTimeout(this.windowResizeThrottle);
    this.windowResizeThrottle = setTimeout(() => {
      if (!this.uiFullscreenEnabled) {
        this.spinnerNode.classList.remove("fullscreen");
      }

      this.loadLayerContent()
        .then(() => {
          this.recalculateLayout(() => {
            this.applyTransform();
          });
        })
        .catch(console.error);
    }, 250);
  }

  /**
   *
   * @returns {Promise<void>}
   */
  async loadLayerContent() {
    this.loaderLayer.visibility = true;
    const images = Object.values(this.activeLayer.images);
    const imagesMap = this.activeLayer.images;
    const firstRowImages = [];
    const startIndex = this.activeLayer.default_row - 1;

    // Load the first row
    for (let i = 0; i < this.activeLayer.columns; i++) {
      const image = imagesMap[`${i + 1}-${startIndex + 1}`];
      if (!image) continue;

      const imageRequest = image.url
        ? CACHE_MANAGER.image(image.url)
        : CACHE_MANAGER.image(
            `${this.imagesBaseUrl}/${image.hash}`,
            this.initialImageSize,
          );

      firstRowImages.push(imageRequest);
    }

    await Promise.all(firstRowImages);

    // Load other rows
    for (const image of images) {
      if (image.url) {
        CACHE_MANAGER.image(image.url);
      } else {
        CACHE_MANAGER.image(
          `${this.imagesBaseUrl}/${image.hash}`,
          this.initialImageSize,
        );
      }
    }

    await this.renderInitialContent();
  }

  /**
   *
   * @returns {Promise<unknown>}
   */
  renderInitialContent() {
    return new Promise((resolve) => {
      if (SPINNABLE_LAYERS.includes(this.activeLayer.type)) {
        this.updateViewerImage(resolve);
      }

      this.loaderLayer.visibility = false;
    });
  }

  setSpinnerColumn(column) {
    let value = parseInt(column);

    value = value < 1 ? this.activeLayer.columns : value;
    value = value > this.activeLayer.columns ? 1 : value;

    this.activeColumn = value;
    this.updateViewerImage();
  }

  setSpinnerRow(row) {
    let value = parseInt(row);

    value = Math.max(value, 1);
    value = Math.min(value, this.activeLayer.rows);

    this.activeRow = value;
    this.updateViewerImage();
  }

  disableFeaturesOnSpinning() {
    if (this.spinningStarted) {
      this.smoothTransform = false;
      this.uiControlsEnabled = false;
      this.uiHotspotsEnabled = false;
    }
  }

  onDragStart(event) {
    event.preventDefault();
    if (
      this.carouselLayer.isOpen ||
      this.controlsLayer.host.contains(event.target)
    ) {
      return;
    }

    this.dragEvent = event;
    this.startTouchEvent = event;
    this.spinningStarted = true;
  }

  onMouseMove(event) {
    cancelAnimationFrame(this.onMouseMoveThrottle);
    this.onMouseMoveThrottle = requestAnimationFrame(() => {
      this.disableFeaturesOnSpinning();
      const fromX = this.dragEvent ? this.dragEvent.clientX : null;
      const fromY = this.dragEvent ? this.dragEvent.clientY : null;

      const tillX = event.clientX;
      const tillY = event.clientY;

      if (this.zoomScale === 1)
        this.handleSpin(event, fromX, fromY, tillX, tillY);
      else if (this.zoomScale > 1) {
        this.handleTransform(event, fromX, fromY, tillX, tillY);
      }
    });
  }

  onTouchMove(event) {
    event.preventDefault();

    cancelAnimationFrame(this.onTouchMoveThrottle);
    this.onTouchMoveThrottle = requestAnimationFrame(() => {
      this.disableFeaturesOnSpinning();

      if (event.touches.length < 2) this.onTouchMoveDrag(event);
      if (event.touches.length > 1) this.onTouchMovePinch(event);
    });
  }

  /**
   * Handle touch event for dragging.
   * @param {TouchEvent} event
   */
  onTouchMoveDrag(event) {
    const fromX = this.dragEvent ? this.dragEvent.touches[0].clientX : null;
    const fromY = this.dragEvent ? this.dragEvent.touches[0].clientY : null;

    const tillX = event.touches[0].clientX;
    const tillY = event.touches[0].clientY;

    if (this.zoomScale === 1)
      this.handleSpin(event, fromX, fromY, tillX, tillY);
    else if (this.zoomScale > 1)
      this.handleTransform(event, fromX, fromY, tillX, tillY);
  }

  /**
   * Handle touch event for pinching.
   * @param {TouchEvent} event
   */
  onTouchMovePinch(event) {
    const distanceFrom = getTouchDistance(this.startTouchEvent.touches);
    const distanceTill = getTouchDistance(event.touches);

    // Calculate zoom center relative to image
    const [touch0, touch1] = event.touches;
    const clientX = (touch0.clientX + touch1.clientX) / 2;
    const clientY = (touch0.clientY + touch1.clientY) / 2;

    // Calculate new scale based on pinch ratio
    const scaleFactor = distanceTill / distanceFrom;
    let newScale = this.zoomScale * scaleFactor;

    // Enforce scale bounds
    newScale = Math.max(1, Math.min(this.maxZoom, newScale));

    this.startTouchEvent = event;

    this.setZoomScale(clientX, clientY, newScale);
  }

  onDragEnd(event) {
    event.preventDefault();

    this.dragEvent = null;
    this.startTouchEvent = null;
    this.spinningStarted = false;

    this.smoothTransform = true;
    this.uiControlsEnabled = true;

    if (this.zoomScale <= 1.5) {
      this.uiHotspotsEnabled = true;
    }
  }

  /**
   * update the activeImage based on the active row and column of
   * the activeLayer and calls the render update
   * @param {Function} [callback]
   */
  updateViewerImage(callback) {
    this.activeImage =
      this.activeLayer.images[`${this.activeColumn}-${this.activeRow}`];
    this.renderer.update(this.activeImage, callback);
  }

  handleSpin(event, fromX, fromY, tillX, tillY) {
    const containerX = 1920 / this.uiWidth;
    const offsetX = fromX
      ? getDragOffset(containerX, fromX, tillX, this.spinningSpeed)
      : 0;

    const containerY = 1080 / this.uiHeight;
    const offsetY = fromY
      ? getDragOffset(containerY, fromY, tillY, this.spinningSpeed)
      : 0;

    if (offsetX !== 0) this.setSpinnerColumn(this.activeColumn + offsetX);
    if (offsetX !== 0) this.dragEvent = event;

    if (offsetY !== 0) this.setSpinnerRow(this.activeRow - offsetY);
    if (offsetY !== 0) this.dragEvent = event;
  }

  handleTransform(event, fromX, fromY, tillX, tillY) {
    if (fromX === null || fromY === null) return;

    const pointX = (this.zoomPointX || 0) + (tillX - fromX);
    const pointY = (this.zoomPointY || 0) + (tillY - fromY);

    this.setZoomPoint(pointX, pointY);

    this.dragEvent = event;
  }

  onWheel(event) {
    event.preventDefault();
    if (!SPINNABLE_LAYERS.includes(this.activeLayer.type)) {
      return;
    }

    const delta = Math.sign(event.deltaY);
    const scale = Math.round((this.zoomScale - delta / 5) * 100) / 100;

    this.setZoomScale(
      event.clientX - this.uiX,
      event.clientY - this.uiY,
      scale,
    );
  }

  setZoomScale(x, y, scale) {
    const prevScale = this.zoomScale;
    this.zoomScale = parseFloat(
      Math.min(Math.max(scale, 1), this.maxZoom).toFixed(3),
    );

    if (this.zoomScale <= 1.015) this.zoomScale = 1;

    const { pointX, pointY } = this.zoomHandler.getZoomPoint(
      x,
      y,
      this.zoomScale,
      prevScale,
    );

    this.setZoomPoint(pointX, pointY);
  }

  setZoomPoint(pointX, pointY) {
    cancelAnimationFrame(this.zoomPointThrottle);

    this.zoomHandler.setZoomPoint(pointX, pointY, this.zoomScale);

    this.zoomPointThrottle = requestAnimationFrame(() => {
      this.applyTransform();
    });
  }

  getTransform() {
    return this.zoomHandler.getTransform();
  }

  getBoundingBoxDimension() {
    const { width, height } = this.generateContainedDimensions();
    const w = width * this.boundingBoxWidthRatio;
    const h = height * this.boundingBoxHeightRatio;

    return {
      width: w,
      height: h,
    };
  }

  generateBoundingBoxCss() {
    const { width, height } = this.getBoundingBoxDimension();

    return [
      `width: ${width}px`,
      `height: ${height}px`,
      `margin-top: ${-((this.boundingBoxOffsetTop / 100) * height)}px`,
      `margin-bottom: ${-(this.boundingBoxOffsetBottom / 100) * height}px`,
      `margin-left: ${-(this.boundingBoxOffsetLeft / 100) * width}px`,
      `margin-right: ${-(this.boundingBoxOffsetRight / 100) * width}px`,
    ].join(";");
  }

  /**
   *
   * @param {object} transform
   */
  applyTransformToViewerLayer(transform) {
    let finalTransform = transform;
    /*
     * Apply scale css to viewer only when the full res image is not
     * available yet
     */
    if (
      this.zoomLayerSubject.rendered &&
      this.zoomLayerSubject.style.opacity !== "0"
    ) {
      finalTransform = { ...transform, opacity: 0 };
    }

    this.viewerLayer.animate(finalTransform);
  }

  applyTransformToHotspotsLayer(transform) {
    let finalTransform = transform;
    // hide hotspots after zoom scale is bigger than 1.5
    if (this.zoomScale > 1.5) {
      this.uiHotspotsEnabled = false;
      finalTransform = { ...transform, opacity: 0 };
    } else if (!this.spinningStarted) {
      this.uiHotspotsEnabled = true;
      finalTransform = { ...transform, opacity: 1 };
    }

    this.hotspotsLayer.animate(finalTransform);
  }

  applyTransform() {
    const transform = this.getTransform();
    this.dispatchEvent(
      new CustomEvent("framemorph", {
        composed: true,
        detail: {
          scale: this.zoomScale,
          transform: transform,
          smoothTransform: this.smoothTransform,
        },
      }),
    );

    // Apply zoom layer css only when the zoom scale is bigger than one
    if (this.zoomScale > 1.01) {
      /*
       * Consider zoom layer not rendered if it's subject src
       * is different from the active image high resolution url
       */
      if (this.zoomLayerSubject.src !== this.activeImage.highResolutionUrl) {
        this.zoomLayerSubject.rendered = false;
      }

      this.applyTransformToViewerLayer(transform);
      this.applyTransformToHotspotsLayer(transform);

      let finalTransform = transform;

      if (this.zoomLayerSubject.rendered) {
        this.zoomLayer.subject.style.opacity = 1;
        finalTransform = { ...transform, opacity: 1 };
      }

      this.zoomLayer.animate(finalTransform);

      this.renderer.upscale();
    } else {
      this.renderer.abortUpscaling();

      this.viewerLayer.animate(transform);
      this.viewerLayer.subject.style.opacity = 1;
      this.zoomLayer.animate(transform);
      this.zoomLayer.subject.style.opacity = 0;
      this.hotspotsLayer.animate({ ...transform, opacity: 1 });

      this.uiHotspotsEnabled = true;
    }
  }

  /**
   *
   * @param {object[]} slides
   * @param {DOMRect} [expandFrom] -  Expand animation from here
   * @returns {Promise<void>}
   */
  async showCarousel(slides, expandFrom) {
    this.uiBusyEnabled = true;
    this.carouselLayer.ready();

    await this.carouselLayer.setSlides(slides);
    this.uiBusyEnabled = false;

    if (slides.length > 1) {
      this.carouselLayer.sliderArrowsVisibility = true;
    }

    if (expandFrom) {
      this.carouselLayer.expandFrom(expandFrom);
    } else {
      this.carouselLayer.slideUp();
    }
  }

  async setPoster() {
    const image = this.activeImage || {
      url: this.placeholder,
    };

    if (!this.placeholder && !this.activeImage) {
      throw new Error(
        "Placeholder is required: make sure the data that is provided has a `placeholder` property",
      );
    }

    const poster = await CACHE_MANAGER.image(
      image.url || `${this.imagesBaseUrl}/${image.hash}`,
      !image.url ? RESOLUTION_1080P : null,
    );

    return this.updatePosterImage(poster);
  }

  async updatePosterImage(poster) {
    this.flashScreenNode.src = poster;

    return await this.flashScreenNode.decode().then(() => {
      this.spinnerNode.classList.remove(
        "has-portrait-content",
        "has-landscape-content",
      );

      if (
        this.flashScreenNode.naturalWidth <= this.flashScreenNode.naturalHeight
      ) {
        this.spinnerNode.classList.add("has-portrait-content");
      } else {
        this.spinnerNode.classList.add("has-landscape-content");
      }

      if (this.viewerLayer.subject instanceof HTMLVideoElement) {
        this.viewerLayer.subject.poster = poster;
      } else {
        this.viewerLayer.subject.src = poster;
      }

      return this.spinnerNode.className;
    });
  }

  applyViewerMode(overrideViewerMode) {
    let finalViewerMode = overrideViewerMode ?? this.defaultViewerMode;

    if (
      this.boundingBox &&
      !overrideViewerMode &&
      !(this.boundingBox instanceof Array)
    ) {
      finalViewerMode = VIEWER_MODE_BOUNDING_BOX;
    }

    if (finalViewerMode === VIEWER_MODE_BOUNDING_BOX) {
      if (!this.boundingBox) {
        throw new Error(
          "'bounding-box' viewer mode is not supported. Data does not contain bounding box",
        );
      }

      this.applyBoundingBox();
    }

    this.viewerMode = finalViewerMode;
  }

  applyBoundingBox() {
    const values = this.boundingBox.percentages;
    const left = values.top_left_offset[0];
    const top = values.top_left_offset[1];
    const right = values.bottom_right_offset[0];
    const bottom = values.bottom_right_offset[1];
    this.boundingBoxWidthRatio = 1 + (left + right) / values.width;
    this.boundingBoxHeightRatio = 1 + (top + bottom) / values.height;
    this.boundingBoxOffsetTop = top;
    this.boundingBoxOffsetBottom = bottom;
    this.boundingBoxOffsetLeft = left;
    this.boundingBoxOffsetRight = right;
  }

  /**
   * Return contained dimensions based on the image and spinner dimension.
   * Contained dimensions mean that image will fit inside the spinner
   * without cropping.
   * @returns {{width: number, height: number}}
   */
  generateContainedDimensions() {
    const imgAspect =
      this.flashScreenNode.offsetWidth / this.flashScreenNode.offsetHeight;

    // Get container dimensions
    const containerWidth = this.spinnerNode.offsetWidth;
    const containerHeight = this.spinnerNode.offsetHeight;
    const containerAspect = containerWidth / containerHeight;

    // Variables for new dimensions
    let newWidth, newHeight;

    // Determine scaling based on aspect ratios
    if (imgAspect > containerAspect) {
      // Image is wider than container: fit to width
      newWidth = containerWidth;
      newHeight = newWidth / imgAspect;
    } else {
      // Image is taller than container: fit to height
      newHeight = containerHeight;
      newWidth = newHeight * imgAspect;
    }

    return {
      width: newWidth,
      height: newHeight,
    };
  }

  recalculateLayout(callback) {
    const spinnerRect = this.spinnerNode.getBoundingClientRect();
    this.uiWidth = spinnerRect.width;
    this.uiHeight = spinnerRect.height;
    this.uiX = spinnerRect.x;
    this.uiY = spinnerRect.y;
    this.spinnerNode.classList.remove("stretch-content", "short");
    this.flashScreenNode.style.cssText = "";
    this.zoomHandler.setSceneSize(this.uiWidth, this.uiHeight);

    if (this.uiHeight < 540) {
      this.spinnerNode.classList.add("short");
    }

    requestAnimationFrame(() => {
      let leadImageRect = this.flashScreenNode.getBoundingClientRect();
      const horizontalClip = Math.ceil(
        (this.uiHeight - leadImageRect.height) / 2,
      );

      if (leadImageRect.height < spinnerRect.height) {
        this.spinnerNode.classList.add("stretch-content");
      }

      if (this.viewerMode === VIEWER_MODE_BOUNDING_BOX) {
        this.flashScreenNode.style.cssText = this.generateBoundingBoxCss();
      }

      setTimeout(() => {
        leadImageRect = this.flashScreenNode.getBoundingClientRect();
        this.contentWidth = leadImageRect.width;
        this.contentHeight = leadImageRect.height;
        this.zoomHandler.setContentSize(this.contentWidth, this.contentHeight);
        this.zoomHandler.setZoomPoint(null, null, this.zoomScale);

        const originX = Math.ceil((this.uiWidth - leadImageRect.width) / 2);
        const originY = Math.ceil((this.uiHeight - leadImageRect.height) / 2);

        this.aspectRatioCSS = `${this.viewerLayer.subjectWidth}/${this.viewerLayer.subjectHeight}`;
        this.sceneNode.style.clipPath = `inset(${horizontalClip}px 0)`;
        this.viewportOffset.originX = -originX;
        this.viewportOffset.originY = -originY;
        this.viewportOffset.x = leadImageRect.x - spinnerRect.x;
        this.viewportOffset.y = leadImageRect.y - spinnerRect.y;
        this.controlsLayer.cssText = `aspect-ratio: ${this.aspectRatioCSS}`;

        if (callback) {
          callback();
        }
      }, 100);
    });
  }
}

export default Spinner;
